import { c as _c } from "react/compiler-runtime";
import { IcoCheckCircle16, IcoChevronRight16, IcoWarning16 } from '@onefootprint/icons';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { cx } from 'class-variance-authority';
import { useEffect, useRef, useState } from 'react';
import type { ChatMessage as ChatMessageData } from '../../hooks/use-chat-ws';
import type { JourneyGroupSegment } from '../../utils/chat-segments';
import { isErrorTaskStatus, isJourneyAgent, isTerminalTaskStatus, journeyDisplay, journeyGroupStatus } from '../../utils/journey-attribution';
import ChatMessage, { getToolDisplay } from '../chat-message';
import PinnedCard, { PinnedBody } from '../pinned-card';
type JourneyCardProps = {
  group: JourneyGroupSegment;
  session: ChatSessionResponse;
  toolResults?: Record<string, string>;
  toolErrors?: Record<string, boolean>;
  /**
   * `inline` (default) anchors the card in the scrollback at the spawn and expands its feed in
   * place. `pinned` sits above the composer framed like the pinned todo/question cards. `reference`
   * is the feed stand-in: the same collapsed header, but it never expands inline — clicking calls
   * `onOpen` (to open the sub-agent's transcript in the Background-agents sidebar).
   */
  variant?: 'inline' | 'pinned' | 'reference';
  /** For `reference`: open the sub-agent's transcript in the sidebar. */
  onOpen?: () => void;
  /** Seeds the initial expand state (the header still toggles it). */
  defaultExpanded?: boolean;
};

// task_progress drives the header, not the feed; tool_result/turn_complete render
// nothing (see chat-message), so a bucketed one would leave an empty feed row.
const isFeedFrame = (msg: ChatMessageData): boolean => msg.type !== 'task_progress' && msg.type !== 'tool_result' && msg.type !== 'turn_complete';

/**
 * Collapsible progress card for a journey subagent running as a background task.
 * Collapsed: journey label + live activity line (shimmering while it works, a
 * check/warning glyph once its task reaches a terminal status). Expanded: the
 * journey's own thinking/narration/tool feed. Lifecycle rows (task_progress)
 * drive the header state and are not repeated inside the feed.
 */
const JourneyCard = t0 => {
  const $ = _c(53);
  const {
    group,
    session,
    toolResults: t1,
    toolErrors: t2,
    variant: t3,
    onOpen,
    defaultExpanded: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const toolResults = t5;
  let t6;
  if ($[2] !== t2) {
    t6 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const toolErrors = t6;
  const variant = t3 === undefined ? "inline" : t3;
  const defaultExpanded = t4 === undefined ? false : t4;
  const isReference = variant === "reference";
  const [expanded, setExpanded] = useState(defaultExpanded);
  const feedRef = useRef(null);
  let t7;
  if ($[4] !== group.agent) {
    t7 = isJourneyAgent(group.agent);
    $[4] = group.agent;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const isJourney = t7;
  let t8;
  if ($[6] !== group.messages) {
    t8 = group.messages.some(_temp);
    $[6] = group.messages;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const hasLifecycle = t8;
  const spawnResultLanded = group.spawnId in toolResults || group.spawnId in toolErrors;
  const latestStatus = journeyGroupStatus(group.messages);
  const isDone = isTerminalTaskStatus(latestStatus) || !isJourney && !hasLifecycle && spawnResultLanded;
  const isError = isErrorTaskStatus(latestStatus) || !isJourney && !hasLifecycle && !!toolErrors[group.spawnId];
  const feed = group.messages.filter(isFeedFrame);
  let t9;
  if ($[8] !== group.agent) {
    t9 = journeyDisplay(group.agent);
    $[8] = group.agent;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const {
    label,
    running
  } = t9;
  useEffect(() => {
    const el = feedRef.current;
    if (el && !isDone) {
      el.scrollTop = el.scrollHeight;
    }
  }, [feed.length, isDone, expanded]);
  let t10;
  bb0: {
    if (isError) {
      t10 = isJourney ? `${label} stopped` : "Stopped";
      break bb0;
    }
    if (isDone) {
      t10 = isJourney ? `${label} finished` : "Finished";
      break bb0;
    }
    for (let i = group.messages.length - 1; i >= 0; i--) {
      const m_0 = group.messages[i];
      if (m_0.type === "task_progress" && m_0.data.description) {
        t10 = m_0.data.description;
        break bb0;
      }
      if (m_0.type === "tool_use") {
        const info = getToolDisplay(m_0.tool, m_0.input);
        if (info?.label) {
          t10 = info.label;
          break bb0;
        }
      }
    }
    t10 = `${running}…`;
  }
  const activityLine = t10;
  let t11;
  if ($[10] !== isReference || $[11] !== onOpen) {
    t11 = isReference ? onOpen : () => setExpanded(_temp2);
    $[10] = isReference;
    $[11] = onOpen;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = isReference ? undefined : expanded;
  const t13 = isDone && (isError ? <span role="img" aria-label="Journey stopped" className="shrink-0 inline-flex"><IcoWarning16 color="error" /></span> : <IcoCheckCircle16 className="shrink-0 text-primary" />);
  let t14;
  if ($[13] !== label) {
    t14 = <span className="text-label-3 text-primary shrink-0">{label}</span>;
    $[13] = label;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const t15 = isError ? "text-error" : "text-tertiary group-hover/jc:text-secondary";
  const t16 = !isDone && "text-shimmer";
  let t17;
  if ($[15] !== t15 || $[16] !== t16) {
    t17 = cx("text-caption-1 truncate min-w-0", t15, t16);
    $[15] = t15;
    $[16] = t16;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] !== activityLine || $[19] !== t17) {
    t18 = <span className={t17}>{activityLine}</span>;
    $[18] = activityLine;
    $[19] = t17;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  const t19 = !isReference && expanded && "rotate-90";
  let t20;
  if ($[21] !== t19) {
    t20 = cx("shrink-0 ml-auto text-quaternary transition-transform", t19);
    $[21] = t19;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] !== t20) {
    t21 = <IcoChevronRight16 className={t20} />;
    $[23] = t20;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  let t22;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t13 || $[28] !== t14 || $[29] !== t18 || $[30] !== t21) {
    t22 = <button type="button" onClick={t11} className="group/jc flex items-center gap-2 w-full max-w-full text-left cursor-pointer" aria-expanded={t12}>{t13}{t14}{t18}{t21}</button>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t18;
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  const toggle = t22;
  let t23;
  if ($[32] !== isDone) {
    t23 = cx("mt-3 mb-1 flex flex-col gap-2 border-l border-solid border-tertiary pl-3", {
      "ml-2": isDone
    });
    $[32] = isDone;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  const railClassName = t23;
  let t24;
  if ($[34] !== session || $[35] !== toolErrors || $[36] !== toolResults) {
    t24 = (msg, idx) => <li key={feedKey(msg, idx)} className="min-w-0"><ChatMessage message={msg} session={session} toolResult={msg.type === "tool_use" ? toolResults[msg.id] : undefined} toolResultIsError={msg.type === "tool_use" ? !!toolErrors[msg.id] : undefined} hideIcon={true} /></li>;
    $[34] = session;
    $[35] = toolErrors;
    $[36] = toolResults;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  const feedItems = feed.map(t24);
  let t25;
  if ($[38] !== expanded || $[39] !== feed || $[40] !== feedItems || $[41] !== railClassName || $[42] !== variant) {
    t25 = expanded && feed.length > 0 && (variant === "pinned" ? <PinnedBody as="ol" ref={feedRef} itemCount={feed.length} className={railClassName}>{feedItems}</PinnedBody> : <ol ref={feedRef} className={railClassName}>{feedItems}</ol>);
    $[38] = expanded;
    $[39] = feed;
    $[40] = feedItems;
    $[41] = railClassName;
    $[42] = variant;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  const feedList = t25;
  if (variant === "pinned") {
    let t26;
    if ($[44] !== expanded || $[45] !== feedList || $[46] !== toggle) {
      t26 = <PinnedCard header={toggle} shrinkable={expanded}>{feedList}</PinnedCard>;
      $[44] = expanded;
      $[45] = feedList;
      $[46] = toggle;
      $[47] = t26;
    } else {
      t26 = $[47];
    }
    return t26;
  }
  let t26;
  if ($[48] !== toggle) {
    t26 = <div className="flex items-center gap-2 w-full max-w-full">{toggle}</div>;
    $[48] = toggle;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== feedList || $[51] !== t26) {
    t27 = <div className="flex flex-col rounded-lg border border-solid border-tertiary px-3 py-2">{t26}{feedList}</div>;
    $[50] = feedList;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  return t27;
};
const feedKey = (msg: ChatMessageData, idx: number): string => {
  if (msg.type === 'tool_use') return msg.id;
  if ((msg.type === 'thinking' || msg.type === 'text') && msg.started_at) return `${msg.type}-${msg.started_at}`;
  return `step-${idx}`;
};
export default JourneyCard;
function _temp(m) {
  return m.type === "task_progress";
}
function _temp2(e) {
  return !e;
}